<template>
  <modal
    ref="modal"
    modal-class="move-initiative"
    title="Move"
    :show="show"
    :hide-footer="true"
    size="lg"
    @hidden="$emit('input', false)"
    :static="true"
    :lazy="true"
  >
    <div class="loader-overlay flex-center" v-if="isSubmitting">
      <spinner />
    </div>
    <div class="text-normal">
      <highlighted-card class="initiatives">
        <div class="mb-1 initiative-item" v-for="initiative in initiatives" :key="initiative.id">
          <b-form-checkbox
            :checked="initiative.isDoneAndClosed"
            unchecked-value="not_accepted"
            :disabled="true"
            class="wrap-hyphenate"
          >
            <span class="bold" v-no-capture>{{ initiative.title }}</span>
          </b-form-checkbox>
          <div class="initiative-parent" v-if="initiative.keyResultId">
            <i class="kr-icon"></i>
            <span
              class="text-medium text-secondary wrap-hyphenate"
              v-if="getKrTitle(initiative.keyResultId)"
              v-no-capture
              >{{ getKrTitle(initiative.keyResultId) }}</span
            >
          </div>
          <div class="initiative-parent" v-else-if="initiative.projectId">
            <project-icon :color="getProject(initiative.projectId).color" />
            <span class="text-medium text-secondary wrap-hyphenate" v-no-capture>{{
              getProject(initiative.projectId).title
            }}</span>
          </div>
        </div>
      </highlighted-card>
      <div class="note text-medium notification-box" v-if="isMixedSelection">
        You have selected a mix of Initiatives and Tasks.
        <ul class="mb-0">
          <li>
            If you move these under a {{ isMovingToProject ? 'Section' : 'KR' }} as Initiatives >
            Selected tasks will be converted to Initiatives
          </li>
          <li>
            If you move these under an Initiative as Tasks > Selected Initiatives will be converted
            to Tasks
          </li>
        </ul>
      </div>
      <div class="target">
        <label class="d-block">Choose where you want to move</label>
        <div class="d-flex">
          <org-selector-dropdown
            :selected="subOrg"
            @change="onSubOrgChange"
            :org-id="orgId"
            v-if="orgsCount > 1"
          />
          <org-team-dropdown
            class="ml-2"
            :parent-team-id="subOrg.id"
            :org-item="orgTarget"
            :show-org-group="isMovingToGoal"
            :entity-context="targetType"
            @change="target = $event"
            :selected="target"
          />
          <initiative-target-type-dropdown
            class="ml-2"
            :selected="targetType"
            @change="onTargetTypeChange"
            v-if="isProjectsEnabled"
          />
          <timeline-filter-dropdown v-if="isMovingToGoal" class="ml-2" v-model="selectedTimeline" />
        </div>
      </div>
      <div class="goals">
        <div v-if="isMovingToProject && projects">
          <span v-if="projects.length === 0">No projects available for the selected filters</span>
          <move-initiative-project-row
            :project="project"
            v-for="project in projects"
            :key="project.id"
            @move="triggerMove($event)"
            v-else
          />
        </div>
        <template v-else-if="goals">
          <div v-if="goals.length === 0" class="text-secondary text-small pt-3 pl-2 ml-1">
            <span v-if="target.id">No team goals available for the selected filters</span>
            <span v-else>No Org goals available for the selected filters</span>
          </div>
          <move-initiative-goal-viewer
            v-for="goal in goals"
            :goal="goal"
            :key="goal.id"
            @move="triggerMove($event)"
          />
        </template>
      </div>
      <confirm-dialog
        ref="addFieldConfirmModal"
        :title="confirmationTitle"
        @ok="forceSubmit"
        ok-title="Submit"
        v-if="unavailableCustomFields && unavailableCustomFields.length > 0"
      >
        <div class="text-medium-1">
          <div>
            Below field{{ unavailableCustomFields.length > 1 ? 's' : '' }}
            {{ unavailableCustomFields.length > 1 ? 'are' : 'is' }} missing in
            {{ teamSpaceName }} "<b>{{ destinationName }}</b
            >"
          </div>
          <ul>
            <li v-for="field in unavailableCustomFields" :key="field.id">
              {{ field.name }}
              <span class="text-small text-secondary pl-1">
                ({{ field.projectId === 'None' ? 'Org' : 'project' }})
              </span>
            </li>
          </ul>
          <h4 class="text-normal-1 mb-3">
            Select appropriate action
          </h4>
          <b-form-radio-group id="radio-slots" v-model="addFieldOption" name="radio-options-slots">
            <b-form-radio value="add-field" name="radio-size">
              Add these fields to {{ teamSpaceName }}
            </b-form-radio>
            <b-form-radio value="do-not-add-field" name="radio-size">
              Don't add. Value of these fields would be visible, but you can't edit them.
            </b-form-radio>
          </b-form-radio-group>
        </div>
      </confirm-dialog>
    </div>
  </modal>
</template>

<script>
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import OrgSelectorDropdown from '@/components/goal/OrgSelectorDropdown'
import HighlightedCard from '@/components/HighlightedCard'
import InitiativeTargetTypeDropdown from '@/components/initiative/InitiativeTargetTypeDropdown.vue'
import MoveInitiativeGoalViewer from '@/components/initiative/MoveInitiativeGoalViewer'
import MoveInitiativeProjectRow from '@/components/initiative/MoveInitiativeProjectRow.vue'
import Modal from '@/components/Modal'
import OrgTeamDropdown from '@/components/OrgTeamDropdown'
import ProjectIcon from '@/components/project/ProjectIcon.vue'
import Spinner from '@/components/Spinner.vue'
import TimelineFilterDropdown from '@/components/TimelineFilterDropdown'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import { featureFlags } from '@/constants/featureFlags'
import { intervalWithId, nowInterval } from '@/interval'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import TeamMixin from '@/mixins/TeamMixin'
import store from '@/store/store'
import debounce from '@/utils/debounce'
import getTeamSpaceName from '@/utils/getTeamSpaceName'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import { BFormCheckbox, BFormRadio, BFormRadioGroup } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

// TODO: extract combination of OrgSelectorDropdown,OrgTeamDropdown, TimelineFilterDropdown. Same can be used in MoveInitiative, MoveKR, and ParentGoalsAlignment

export default {
  name: 'MoveInitiative',
  props: ['show', 'teamId', 'initiatives'],
  model: {
    prop: 'show'
  },
  components: {
    InitiativeTargetTypeDropdown,
    Spinner,
    ProjectIcon,
    MoveInitiativeProjectRow,
    BFormRadioGroup,
    BFormRadio,
    ConfirmDialog,
    Modal,
    BFormCheckbox,
    MoveInitiativeGoalViewer,
    OrgTeamDropdown,
    TimelineFilterDropdown,
    HighlightedCard,
    OrgSelectorDropdown
  },
  mixins: [TeamMixin, EventBusMixin, FeaturesMixin],
  data() {
    const initiative = this.initiatives[0]
    const orgId = initiative?.organizationId
    const subOrgId = this.getClosestOrgLikeTeamId(initiative.teamId)
    const subOrg = this.$store.getters.teamById(subOrgId)
    const isProjectInitiative = !!this.initiatives[0].projectId
    return {
      target: {},
      destinationParams: {},
      goals: null,
      projects: null,
      orgTarget: { name: 'Org Goals' },
      selectedTimeline: null,
      orgId,
      subOrgId,
      subOrg,
      orgsCount: this.getOrgLikeTeamsOfOrg(orgId).length,
      unavailableCustomFields: null,
      addFieldOption: 'add-field',
      isSubmitting: false,
      targetType: isProjectInitiative ? ENTITY_TYPE.PROJECT : ENTITY_TYPE.GOAL,
      firstInitiative: this.initiatives[0]
    }
  },
  computed: {
    isProjectsEnabled() {
      return !!this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_PROJECTS)
    },
    isMovingToProject() {
      return this.targetType === ENTITY_TYPE.PROJECT
    },
    isMovingToGoal() {
      return this.targetType === ENTITY_TYPE.GOAL
    },
    isMixedSelection() {
      const hasTask = !!this.initiatives.find(x => x.parentId)
      const hasInitiative = !!this.initiatives.find(x => !x.parentId)
      return hasTask && hasInitiative
    },
    teamSpaceName() {
      if (this.isMovingToProject) {
        return 'Project'
      } else {
        return getTeamSpaceName(this.$store.getters.teamById(this.firstInitiative.teamId))
      }
    },
    confirmationTitle() {
      const missingCount = this.unavailableCustomFields.length
      return `${this.unavailableCustomFields.length} field${
        this.unavailableCustomFields.length > 1 ? 's' : ''
      } ${missingCount > 1 ? 'are' : 'is'} missing from destination ${this.teamSpaceName}`
    },
    destinationName() {
      if (this.isMovingToProject) {
        const project = this.$store.getters['project/projectById'](this.destinationParams.projectId)
        return project.title
      } else {
        return this.target.name
      }
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    teams() {
      return this.$store.getters.userTeamsByOrgId(this.orgId)
    }
  },
  methods: {
    ...mapActions(['moveInitiatives', 'getGoal']),
    ...mapActions('project', ['getTeamProjects']),
    getKrTitle(krId) {
      return this.$store.getters.keyResultById(krId)?.title
    },
    getProject(projectId) {
      return this.$store.getters['project/projectById'](projectId)
    },
    async refreshGoals() {
      const goals = await store.dispatch('getGoals', {
        orgId: this.orgId,
        teamId: this.target.id || this.subOrg.id,
        options: {
          intervalStart: this.selectedTimeline.start,
          intervalEnd: this.selectedTimeline.end,
          intervalSubset: this.selectedTimeline.subset,
          scopeId: this.subOrg.id
        }
      })
      this.goals = goals.filter(goal => goal.keyResults.filter(kr => !kr.isArchived).length > 0)
    },
    triggerMove(destinationParams) {
      this.destinationParams = destinationParams
      this.moveInitiative()
    },
    getGroupedInitiatives(listA, listB) {
      return listA.map(initiative => {
        return [initiative, listB.find(i => i.id === initiative.id)]
      })
    },
    async moveInitiative(params) {
      try {
        this.isSubmitting = true
        const fromInitiatives = this.initiatives
        const toInitiatives = await this.moveInitiatives({
          orgId: this.orgId,
          params,
          payload: {
            initiativeIds: this.initiatives.map(i => i.id),
            action: 'move',
            ...this.destinationParams
          }
        })
        const groupedInitiatives = this.getGroupedInitiatives(fromInitiatives, toInitiatives)
        this.eventBusEmit(events.INITIATIVES_MOVED, groupedInitiatives)
        this.$refs.modal.hide()
        this.$emit('moved', groupedInitiatives)
        showToast(`Initiative${this.initiatives.length > 1 ? 's' : ''} moved successfully`)
      } catch (e) {
        this.unavailableCustomFields = e.response?.data?.detail?.unavailableCustomFields
        if (this.unavailableCustomFields?.length > 0) {
          setTimeout(() => {
            this.$refs.addFieldConfirmModal.show()
          }, 300)
        } else {
          showAPIError(e, ['detail', 'name'], undefined, 10000)
        }
      }
      this.isSubmitting = false
    },
    forceSubmit() {
      this.moveInitiative({ hint: this.addFieldOption })
    },
    getFirstTeamOfOrg(subOrg) {
      const teams = this.$store.getters.userTeamsByOrgIdSubOrgId(subOrg.organizationId, subOrg.id)
      return teams.find(x => !x.isArchived && !x.isOrgLike)
    },
    onSubOrgChange(subOrg) {
      this.subOrg = subOrg
      this.target = this.isMovingToProject ? this.getFirstTeamOfOrg(subOrg) : this.orgTarget
    },
    onTargetTypeChange(targetType) {
      this.targetType = targetType
      if (targetType === ENTITY_TYPE.PROJECT && this.target === this.orgTarget) {
        this.target = this.getFirstTeamOfOrg(this.subOrg)
      }
    },
    async refreshProjects() {
      this.projects = await this.getTeamProjects({
        orgId: this.orgId,
        teamId: this.target.id,
        params: {
          expand: 'sections'
        }
      })
    },
    refreshResource: debounce(function() {
      if (this.isMovingToProject) {
        this.refreshProjects()
      } else {
        this.refreshGoals()
      }
    }, 100),
    async fetchGoals(goalIds) {
      const promises = goalIds.map(goalId => {
        const goal = this.$store.getters.goalById(goalId)
        if (!goal) {
          return this.getGoal({
            goalId,
            orgId: this.orgId
          })
        }
        return goal
      })
      const responses = await Promise.allSettled(promises)
      return responses.map(x => x.value).filter(Boolean)
    }
  },
  async mounted() {
    const goalIds = R.uniq(this.initiatives.map(initiative => initiative.goalId).filter(Boolean))
    const goals = await this.fetchGoals(goalIds)
    let goal = goals[0]
    if (goal) {
      this.selectedTimeline = intervalWithId(goal.timeline)
    } else {
      this.selectedTimeline = nowInterval()
    }
    const teamId = this.firstInitiative.teamId
    this.target = this.teams.find(R.propEq('id', teamId))
  },
  watch: {
    target: 'refreshResource',
    subOrg: 'refreshResource',
    selectedTimeline: 'refreshResource',
    targetType: 'refreshResource'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.initiatives {
  @include scrollbarWidth($scrollSecondary);
  max-height: 260px;
  overflow: auto;
}

.goals {
  min-height: 300px;
  padding-left: 12px;
  padding-top: 8px;
}

.target label {
  padding: 16px 12px 12px 18px;
  margin: 0;
}

.initiative-parent {
  margin-left: 24px;
}

.initiative-item:not(:last-child) .initiative-parent {
  padding-bottom: 4px;
  border-bottom: 1px solid $borderSecondary;
}

.kr-icon {
  transform: scale(0.6) translateY(3px);
  margin-right: 5px;
  filter: grayscale(1);
}

.note {
  padding: 14px 18px;
  margin-top: 16px;
}

.loader-overlay {
  position: absolute;
  background: rgba(0, 0, 0, 0.2);
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 10;
}
</style>
<style lang="scss">
.move-initiative .modal-title {
  padding-left: 12px;
}
</style>
